/**
 * Google Drive Picker — wraps `@googleworkspace/drive-picker-element` web component.
 * The user selects files from their Google Drive; selected file IDs + MIME types are
 * sent to the server for download/export via Drive API.
 */

import '@googleworkspace/drive-picker-element';
import { stateful, useState, useEffect } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { ParsedDocument } from './document-parser.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CloudFileEntry {
  id: string;
  name: string;
  mimeType: string;
  source: 'gdrive' | 'onedrive';
  status: 'downloading' | 'parsing' | 'done' | 'error';
  error?: string;
  parsed?: ParsedDocument;
}

interface GooglePickerDoc {
  id: string;
  mimeType: string;
  name: string;
  sizeBytes?: number;
}

export interface GoogleDrivePickerProps {
  googleClientId: string;
  googleApiKey: string;
  googleAppId: string;
  accessToken?: string;
  onFilesSelected: (entries: CloudFileEntry[]) => void;
}

// Map Google Workspace MIME types to our fileType identifiers
const GOOGLE_NATIVE_MIMES: Record<string, string> = {
  'application/vnd.google-apps.document': 'gdoc',
  'application/vnd.google-apps.spreadsheet': 'gsheet',
  'application/vnd.google-apps.presentation': 'gslide',
};

// Map export MIME types to file extensions — ensures parseDocument picks the right parser
const MIME_TO_EXT: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/pdf': '.pdf',
};

/** Ensure file name has the correct extension matching the export format. */
function ensureCorrectExtension(name: string, mimeType: string): string {
  const expectedExt = MIME_TO_EXT[mimeType];
  if (!expectedExt) return name;
  if (name.toLowerCase().endsWith(expectedExt)) return name;
  // Strip any existing extension from Google native names (e.g. "My Doc" has none)
  const dotIdx = name.lastIndexOf('.');
  const base = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  return base + expectedExt;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GoogleDrivePicker = stateful((props: GoogleDrivePickerProps) => {
  const { googleClientId, googleApiKey, googleAppId, accessToken: propToken, onFilesSelected } = props;
  const [loading, setLoading] = useState(false);
  const [oauthToken, setOauthToken] = useState<string | null>(propToken ?? null);
  const [error, setError] = useState<string | null>(null);
  const [pickerActive, setPickerActive] = useState(!!propToken);

  // Sync propToken changes into state (useState only uses initial value)
  useEffect(() => {
    if (propToken) {
      setOauthToken(propToken);
      setPickerActive(true);
    }
  }, [propToken]);

  const handleOauthResponse = (e: CustomEvent) => {
    const token = (e.detail as any)?.access_token;
    if (token) {
      setOauthToken(token);
      setError(null);
    }
  };

  const handleOauthError = (e: CustomEvent) => {
    console.error('Google OAuth error:', e.detail);
    setError('Google sign-in failed. Please try again.');
  };

  const handlePicked = async (e: CustomEvent) => {
    const docs: GooglePickerDoc[] = (e.detail as any)?.docs ?? [];
    if (docs.length === 0) return;

    // Immediately deactivate the picker so re-renders don't trigger another OAuth popup
    setPickerActive(false);
    setLoading(true);
    setError(null);

    // Create entries in 'downloading' state
    const entries: CloudFileEntry[] = docs.map(doc => ({
      id: doc.id,
      name: doc.name,
      mimeType: doc.mimeType,
      source: 'gdrive' as const,
      status: 'downloading' as const,
    }));

    // Notify parent immediately so it can show loading state
    onFilesSelected(entries);

    // Download each file via server proxy
    const results: CloudFileEntry[] = [];
    for (const doc of docs) {
      try {
        const res = await fetch('/api/gdrive/download', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileId: doc.id,
            mimeType: doc.mimeType,
            accessToken: oauthToken,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({ error: 'Request failed' }));
          results.push({ ...entries.find(e => e.id === doc.id)!, status: 'error', error: data.error || `Server error (${res.status})` });
          continue;
        }

        const data = await res.json() as {
          fileName: string;
          fileType: string;
          base64?: string;
          exportMimeType?: string;
        };

        if (data.base64) {
          // Server sent file content as base64 — parse client-side with full table/image extraction
          const binary = atob(data.base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

          // Ensure file name has the correct extension for parser detection
          let fileName = data.fileName || doc.name;
          const exportMime = data.exportMimeType || doc.mimeType;
          fileName = ensureCorrectExtension(fileName, exportMime);

          const file = new File([bytes], fileName, { type: exportMime });

          // Dynamic import to avoid circular dependency
          const { parseDocument } = await import('./document-parser.js');
          const parsed = await parseDocument(file);
          parsed.sourceUrl = `https://drive.google.com/file/d/${doc.id}`;

          // Preserve original Google fileType for UI display purposes
          const googleFileType = GOOGLE_NATIVE_MIMES[doc.mimeType];
          if (googleFileType) {
            parsed.fileType = googleFileType as ParsedDocument['fileType'];
          }

          results.push({ ...entries.find(e => e.id === doc.id)!, status: 'done', parsed });
        } else {
          results.push({ ...entries.find(e => e.id === doc.id)!, status: 'error', error: 'No content returned from server' });
        }
      } catch (err) {
        results.push({
          ...entries.find(e => e.id === doc.id)!,
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to download file',
        });
      }
    }

    setLoading(false);
    onFilesSelected(results);
  };

  const handlePickerCanceled = () => {
    setPickerActive(false);
  };

  if (!googleClientId || !googleApiKey) {
    return html`<span class="text-base-content/40 text-xs italic">Google Drive not configured</span>`;
  }

  // Only render the <drive-picker> after user clicks — prevents auto OAuth popup on page load
  if (!pickerActive) {
    return html`
      <span class="inline-flex items-center gap-1">
        ${loading
          ? html`<span class="loading loading-spinner loading-xs"></span><span class="text-base-content/50 text-xs">Loading Google Drive…</span>`
          : nothing}
        ${error ? html`<span class="text-error text-xs">${error}</span>` : nothing}
      </span>
    `;
  }

  return html`
    <drive-picker
      client-id=${googleClientId}
      app-id=${googleAppId}
      developer-key=${googleApiKey}
      oauth-token=${oauthToken ?? ''}
      multiselect
      @picker-picked=${handlePicked}
      @picker-canceled=${handlePickerCanceled}
      @picker-oauth-response=${handleOauthResponse}
      @picker-oauth-error=${handleOauthError}
    >
      <drive-picker-docs-view
        label="My Drive"
        parent="root"
        include-folders="true"
        select-folder-enabled="false"
      ></drive-picker-docs-view>
      <drive-picker-docs-view
        label="Shared Drives"
        enable-drives="true"
        include-folders="true"
        select-folder-enabled="false"
      ></drive-picker-docs-view>
      <span style="display:none"></span>
    </drive-picker>
    <span class="inline-flex items-center gap-1">
      ${loading
        ? html`<span class="loading loading-spinner loading-xs"></span><span class="text-base-content/50 text-xs">Loading…</span>`
        : nothing}
      ${error ? html`<span class="text-error text-xs">${error}</span>` : nothing}
    </span>
  `;
});
